import React                        from 'react';
import { isNullOrUndefinedOrEmpty } from 'rewire-common';
import { Observe }                  from 'rewire-core';
import {
  EditorType,
  editor,
  defaultPhoneFormat,
  defaultPhoneMask,
  TextField,
  TextEditorProps,
}                                   from 'rewire-ui';
import {
  getNumberString,
  getPhoneString,
}                                   from 'rewire-grid';
import { UdfType }                  from '../DataTypes';
import { makeStyles, Theme }        from '../utility/styles';
import Popover                      from '@material-ui/core/Popover';

export interface IFormPopoverEditorComponentWrapperProps extends TextEditorProps {
  editorType: EditorType;
  EditorComponent: React.FunctionComponent<TextEditorProps>;
}

const useStyle = makeStyles((theme: Theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    minWidth: '300px',
    maxWidth: '500px',
  },
  opened: {
    border: `2px solid ${theme.palette.dialogPrimary.main}`,
    margin: '-1px',
  },
  inputFormControlRoot: {
    width: '100%',
  },
}));

export interface IFormPopoverData {
  title: string;
  display: string;
}

export type TGetFormPopoverData = (value: any, editorType: EditorType, valueType: UdfType, editorOptions: any) => IFormPopoverData;

export function defaultGetFormPopoverData(value: any, editorType: EditorType, valueType: UdfType, editorOptions: any): IFormPopoverData {
  let title   = '';
  let display = '';

  if (!isNullOrUndefinedOrEmpty(value)) {
    switch (editorType) {
      case 'text':
      case 'multitext':
      case 'email':
      case 'static':
        title   = value;
        display = title;
        break;
      case 'number':
        title   = getNumberString(value, editorOptions?.decimals, editorOptions?.thousandSeparator, editorOptions?.fixed) as string;
        display = title;
        break;
      case 'checked':
      case 'switch':
        title   = value ? 'Yes' : 'No';
        display = title;
        break;
      case 'date':
        title   = value.toDateString();
        display = title;
        break;
      case 'time':
        title   = value;
        display = title;
        break;
      case 'phone': {
        const phoneFormat = editorOptions?.format ?? defaultPhoneFormat;
        const phoneMask   = editorOptions?.mask ?? defaultPhoneMask;
        title            = getPhoneString(value, phoneFormat, phoneMask) as string;
        display          = title;
        break;
      }
      case 'auto-complete':
      case 'select':
        if (valueType === 'boolean'){
          title   = value ? 'Yes' : 'No';
          display = title;
        } else {
          title   = value.name ?? value;
          display = title;
        }
        break;
      case 'multiselectautocomplete':
      case 'multiselect': {
        const v  = (value.map((v: any) => v.name ?? v) as string[]);
        title   = v.join(', ');
        display = v.length > 3 ? `${v.slice(0, 3).join(', ')}, ....` : title;
        break;
      }
    }
  }

  return ({ title: title, display: display });
}

export function getCreateFormPopoverEditorFn(getFormPopoverData?: TGetFormPopoverData, EditorComponentWrapper?: (props: IFormPopoverEditorComponentWrapperProps) => JSX.Element) {
  return ((editorType: EditorType, valueType: UdfType, editorOptions: any) => {
    const EditorComponent = editor(editorType, {...editorOptions, updateOnChange: true});
    const Editor          = (props: TextEditorProps) => {
      const styles                    = useStyle();
      const [opened, setOpened]       = React.useState<boolean>(false);
      const [origValue, setOrigValue] = React.useState<any>(props.field?.value);
      const textRef                   = React.useRef<HTMLDivElement>(null);

      if (props.field) {
        props.field.autoFocus = true;
      }

      function open() {
        setOpened(true);
      }

      function accept() {
        setOrigValue(props.field?.value);
        setOpened(false);
        requestAnimationFrame(() => textRef.current?.focus());
      }

      function reject() {
        props.onValueChange?.(origValue);
        setOpened(false);
        requestAnimationFrame(() => textRef.current?.focus());
      }

      function textDisplayKeyHandler(evt: React.KeyboardEvent<HTMLInputElement>) {
        if (evt.key.length === 1 && !opened) {
          requestAnimationFrame(() => open());
          evt.preventDefault();
        }
      }

      function popoverKeyHandler(evt: React.KeyboardEvent<HTMLInputElement>) {
        switch(evt.key) {
          case 'Enter':
            (evt.target as HTMLElement)?.blur?.();
            accept();
            break;
          case 'Escape':
            reject();
            break;
        }
        evt.stopPropagation();
      }

      return (
        <Observe render={() => {
          const field              = props.field;
          const fValue             = field?.value;
          const fLabel             = field?.label;
          const fError             = field?.error as any;
          const popoverDataGetter  = getFormPopoverData || defaultGetFormPopoverData;
          const { title, display } = popoverDataGetter(fValue, editorType, valueType, editorOptions);
          return (
            <>
              <div className={props.className} ref={textRef} onClick={() => {if (!opened) open();}}>
                <TextField
                  classes={{formControlRoot: styles.inputFormControlRoot}}
                  value={display}
                  tooltip={() => title}
                  label={fLabel}
                  variant={'outlined'}
                  tabIndex={opened ? undefined : 0}
                  error={fError}
                  readOnly={true}
                  onValueChange={() => {}}
                  onKeyDown={textDisplayKeyHandler}
                  endOfTextOnFocus={true}
                />
              </div>
              <Popover
                classes={{paper: styles.paper}}
                open={opened}
                anchorEl={textRef?.current}
                marginThreshold={5}
                anchorOrigin={{vertical: 'center', horizontal: 'left'}}
                transformOrigin={{vertical: 'center', horizontal: 'left'}}
                onKeyDown={popoverKeyHandler}
                onBackdropClick={(evt) => {
                  accept();
                  evt.stopPropagation();
                  evt.preventDefault();
                }}
              >
                {EditorComponentWrapper
                  ? <EditorComponentWrapper {...props} editorType={editorType} EditorComponent={EditorComponent} />
                  : <EditorComponent {...props} />

                }

              </Popover>
            </>
          );
        }} />
      );
    };

    return Editor;
  });
}
